import { OrderCommentsSection } from "@/components/commerce/order-comments-section";
import { OrderLinesTable } from "@/components/commerce/order-lines-table";
import { OrderReturnsSection } from "@/components/commerce/order-returns-section";
import { OrderSidebar } from "@/components/commerce/order-sidebar";
import { OrderStatusPicker } from "@/components/commerce/order-status-picker";
import { DetailSkeleton } from "@/components/commerce/table-skeleton";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useOnboarding } from "@/features/onboarding";
import { ExportTemplateService } from "@/gen/omnios/v1/export_template_connect";
import {
	useConfirmOrder,
	useExportTemplates,
	useGetOrder,
	useUpdateOrder,
} from "@/hooks/use-commerce";
import { transport } from "@/lib/grpc";
import { useEscapeBack } from "@/hooks/use-escape-back";
import { useBreadcrumbs } from "@/lib/use-breadcrumbs";
import { createClient } from "@connectrpc/connect";
import { Link, createFileRoute } from "@tanstack/react-router";
import {
	AlertTriangle,
	ArrowLeft,
	Check,
	Download,
	Loader2,
	Save,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

export const Route = createFileRoute("/commerce/orders/$id")({
	component: OrderDetailPage,
});

const exportClient = createClient(ExportTemplateService, transport);

function OrderDetailPage() {
	const { t } = useTranslation("orders");
	const { id } = Route.useParams();
	const { completeStep } = useOnboarding();

	useEscapeBack("/commerce/orders");
	const { data: order, isLoading, error } = useGetOrder(id);
	const updateMut = useUpdateOrder(id);
	const confirmMut = useConfirmOrder(id);
	const { data: exportTemplates } = useExportTemplates();

	const [title, setTitle] = useState("");
	const [dirty, setDirty] = useState(false);
	const [exporting, setExporting] = useState(false);

	useEffect(() => {
		if (order) {
			setTitle(order.title);
			setDirty(false);
		}
	}, [order]);

	useBreadcrumbs([
		{ label: t("title"), href: "/commerce/orders" },
		{ label: order ? `#${order.seqNo} ${order.title}` : "…" },
	]);

	async function handleExport(templateId?: string) {
		setExporting(true);
		try {
			const res = await exportClient.exportOrder({
				orderId: id,
				templateId: templateId ?? "",
			});
			if (res.fileUrl) {
				window.open(res.fileUrl, "_blank");
				completeStep("exportDone", 4);
			}
		} catch {
			toast.error(t("export_error"));
		}
		setExporting(false);
	}

	if (isLoading) return <DetailSkeleton />;

	if (error || !order) {
		return (
			<div className="py-16 text-center text-sm text-muted-foreground">
				{t("not_found")}
			</div>
		);
	}

	const lines = order.lines ?? [];
	const isOverdue = order.statusSlaDeadline
		? new Date(Number(order.statusSlaDeadline.seconds) * 1000) < new Date()
		: false;

	return (
		<div className="space-y-6 p-6 h-full overflow-auto">
			{/* Draft Banner */}
			{order.isDraft && (
				<Card className="border-primary/20 bg-primary/5">
					<CardContent className="flex flex-wrap items-center justify-between gap-3 p-4">
						<div className="space-y-1">
							<p className="text-sm font-medium">{t("draft_banner_title")}</p>
							<p className="text-sm text-muted-foreground">
								{t("draft_banner_description")}
							</p>
						</div>
						<Button
							size="sm"
							onClick={() =>
								confirmMut.mutate(undefined, {
									onError: () => toast.error(t("confirm_error")),
								})
							}
							disabled={confirmMut.isPending}
						>
							{confirmMut.isPending ? (
								<Loader2 className="mr-1 h-4 w-4 animate-spin" />
							) : (
								<Check className="mr-1 h-4 w-4" />
							)}
							{t("confirm_order")}
						</Button>
					</CardContent>
				</Card>
			)}

			{/* SLA Warning */}
			{isOverdue && order.statusSlaDeadline && (
				<Card className="border-destructive/30 bg-destructive/5">
					<CardContent className="flex items-center gap-3 p-4 text-sm">
						<AlertTriangle className="h-4 w-4 text-destructive" />
						<div className="min-w-0">
							<p className="font-medium">{t("sla_warning_title")}</p>
							<p className="text-muted-foreground">
								{t("sla_warning_description")}
							</p>
						</div>
					</CardContent>
				</Card>
			)}

			{/* Header */}
			<div className="flex items-center gap-3">
				<Link
					to="/commerce/orders"
					className="rounded-md p-1.5 text-muted-foreground hover:bg-accent"
					aria-label={t("back_to_orders")}
				>
					<ArrowLeft className="h-4 w-4" />
				</Link>
				<div className="flex-1 min-w-0">
					<div className="flex items-center gap-2">
						<span className="text-sm text-muted-foreground">
							#{String(order.seqNo)}
						</span>
						<OrderStatusPicker order={order} />
					</div>
					<Input
						value={title}
						onChange={(e) => {
							setTitle(e.target.value);
							setDirty(true);
						}}
						className="mt-1 text-lg font-semibold border-0 px-0 shadow-none focus-visible:ring-0 h-auto"
					/>
				</div>
				<div className="flex items-center gap-2">
					{dirty && (
						<Button
							size="sm"
							onClick={() =>
								updateMut.mutate(
									{ title },
									{
										onSuccess: () => setDirty(false),
										onError: () => toast.error(t("update_error")),
									},
								)
							}
							disabled={updateMut.isPending}
						>
							{updateMut.isPending ? (
								<Loader2 className="h-4 w-4 animate-spin mr-1" />
							) : (
								<Save className="h-4 w-4 mr-1" />
							)}
							{t("common:save")}
						</Button>
					)}
					<Button
						size="sm"
						variant="outline"
						onClick={() => window.print()}
					>
						{t("print")}
					</Button>
					<DropdownMenu>
						<DropdownMenuTrigger asChild>
							<Button
								size="sm"
								variant="outline"
								disabled={exporting}
								data-onboarding="export-order"
							>
								{exporting ? (
									<Loader2 className="h-4 w-4 animate-spin mr-1" />
								) : (
									<Download className="h-4 w-4 mr-1" />
								)}
								{t("export")}
							</Button>
						</DropdownMenuTrigger>
						<DropdownMenuContent align="end">
							<DropdownMenuItem onClick={() => handleExport()}>
								{t("export_default")}
							</DropdownMenuItem>
							{(exportTemplates?.templates?.length ?? 0) > 0 && (
								<>
									<DropdownMenuSeparator />
									{exportTemplates!.templates.map((tpl) => (
										<DropdownMenuItem
											key={tpl.id}
											onClick={() => handleExport(tpl.id)}
										>
											{tpl.name}
											<span className="ml-auto text-xs text-muted-foreground uppercase">
												{tpl.format || "xlsx"}
											</span>
										</DropdownMenuItem>
									))}
								</>
							)}
						</DropdownMenuContent>
					</DropdownMenu>
				</div>
			</div>

			{/* Main content: Tabs + Sidebar */}
			<div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
				<div className="lg:col-span-3">
					<Tabs defaultValue="lines" className="space-y-4">
						<TabsList>
							<TabsTrigger value="lines">
								{t("tab_lines", {
									defaultValue: "Строки",
								})}
							</TabsTrigger>
							<TabsTrigger value="returns">
								{t("tab_returns", {
									defaultValue: "Возвраты",
								})}
							</TabsTrigger>
							<TabsTrigger value="activity">
								{t("tab_activity", {
									defaultValue: "Активность",
								})}
							</TabsTrigger>
						</TabsList>

						<TabsContent value="lines" className="space-y-4">
							<OrderLinesTable
								orderId={order.id}
								lines={lines}
								isDraft={order.isDraft}
							/>
							{order.description && (
								<Card>
									<CardContent className="p-4 text-sm whitespace-pre-wrap">
										{order.description}
									</CardContent>
								</Card>
							)}
						</TabsContent>

						<TabsContent value="returns">
							<OrderReturnsSection orderId={order.id} lines={lines} />
						</TabsContent>

						<TabsContent value="activity">
							<OrderCommentsSection orderId={order.id} />
						</TabsContent>
					</Tabs>
				</div>

				<OrderSidebar order={order} />
			</div>
		</div>
	);
}
